import { type ComponentProps, lazy, type ReactNode, Suspense, useContext, useEffect } from 'react';

import type {
  TAccordionContainerElement,
  TAccordionContentElement,
  TAccordionElement,
  TAccordionTitleElement,
  TAdSlotElement,
  TAudioPlayerElement,
  TAuthorsElement,
  TBulletedListElement,
  TButtonElement,
  TColumnElement,
  TColumnsElement,
  TContactFormElement,
  TContainerElement,
  TContentGateElement,
  TContentGatePopupElement,
  TDividerElement,
  TEmailCaptureElement,
  TEmailCapturePopupElement,
  TEmbedElement,
  TextLeafProps,
  THeadingElement,
  TIconElement,
  TImageElement,
  TListItemElement,
  TLoginElement,
  TLoginModalElement,
  TNumberedListElement,
  TParagraphElement,
  TPodcastPlatformsElement,
  TPodcastTranscriptElement,
  TPodcastVideoElement,
  TPostActionsElement,
  TPostCommentsElement,
  TPostContentElement,
  TPostElement,
  TPostHeaderElement,
  TPostNavigationElement,
  TPricingElement,
  TProductReviewsElement,
  TProductsElement,
  TProgressIndicatorElement,
  TRecommendationsElement,
  TRecommendationsModalElement,
  TRecommendedPostsElement,
  TResetPasswordElement,
  TRichTextElement,
  TRootElement,
  TRSSFeedElement,
  TRSSFeedSearchElement,
  TSectionElement,
  TSignupElement,
  TSignupModalElement,
  TSocialsElement,
  TSubscriberGateElement,
  TSurveyElement,
  TTagsElement,
  TTestimonialsElement,
  TUpdatePasswordElement,
} from '@shared/dream-components';
import { setUnmappedStructureElement } from '@shared/dream-components/src/serializer/getStructureElement';
import { QueryClientContext } from '@tanstack/react-query';
import { ClientOnly } from 'remix-utils/client-only';

import { ComponentErrorBoundary } from '~/components/ComponentErrorBoundary';
import { getCommentsVisibility } from '~/context/PostPageContext';
import { usePostPageContext } from '~/context/usePostPageContext';
import type { NodeContent } from '~/interfaces/page';
import { prefetchCommentThread } from '~/routes/community/posts/$id/_components/comments/_hooks/useCommentThread';

import { SITE_MODAL_NODE_TYPES, SiteModalSlotContext } from './nodes/context/SiteModalSlotContext';

import { lazyBlock } from './lazyBlock';
import { Accordion, AccordionContainer, AccordionContent, AccordionTitle } from './nodes/Accordion';
import AudioPlayer from './nodes/AudioPlayer';
import Button from './nodes/Button/';
import Column from './nodes/Column';
import Columns from './nodes/Columns';
import Container from './nodes/Container';
import { ContentGate } from './nodes/ContentGate';
import { ContentGatePopup } from './nodes/ContentGatePopup';
import Divider from './nodes/Divider';
import Doc from './nodes/Doc';
import EmailCapture from './nodes/EmailCapture';
import EmailCapturePopup from './nodes/EmailCapturePopup';
import Embed from './nodes/Embed';
import Heading from './nodes/Heading';
import Icon from './nodes/Icon';
import Image from './nodes/Image';
import { BulletedList, ListItem, OrderedList } from './nodes/Lists';
import Login from './nodes/Login';
import LoginModal from './nodes/LoginModal';
import Paragraph from './nodes/Paragraph';
import { PostActions } from './nodes/PostActions';
import PostCommentsSkeleton from './nodes/PostCommentsSkeleton';
import PostContainer from './nodes/PostContainer';
import { PostContent } from './nodes/PostContent';
import PostHeader from './nodes/PostHeader';
import PricingSkeleton from './nodes/PricingSkeleton';
import ProgressIndicator from './nodes/ProgressIndicator';
import type { ProprietaryFooterNode } from './nodes/ProprietaryFooter';
import { ProprietaryFooter } from './nodes/ProprietaryFooter';
import Recommendations from './nodes/Recommendations';
import RecommendationsModal from './nodes/RecommendationsModal';
import RecommendedPosts from './nodes/RecommendedPosts';
import RichText from './nodes/RichText';
import RSSFeed from './nodes/RSSFeed';
import Section from './nodes/Section';
import Signup from './nodes/Signup';
import SignupModal from './nodes/SignupModal';
import Socials from './nodes/Socials';
import SubscriberGate from './nodes/SubscriberGate';
import Tags from './nodes/Tags';
import Testimonials from './nodes/Testimonials';
import Text from './nodes/Text';

// The rare blocks' styles stay static, so their server HTML is styled before their chunks load.
import '@shared/dream-components/src/authorsElement/Authors.css';
import '@shared/dream-components/src/blockquoteElement/Blockquote.css';
import '@shared/dream-components/src/codeBlockElement/CodeBlock.css';
import '@shared/dream-components/src/contactFormElement/ContactForm.css';
import '@shared/dream-components/src/figcaptionElement/Figcaption.css';
import '@shared/dream-components/src/footerElement/Footer.css';
import '@shared/dream-components/src/footerElement/PublicationDetails.css';
import '@shared/dream-components/src/genericEmbedElement/GenericEmbed.css';
import '@shared/dream-components/src/podcastPlatformsElement/PodcastPlatforms.css';
import '@shared/dream-components/src/podcastTranscriptElement/PodcastTranscript.css';
import '@shared/dream-components/src/productsElement/Products.css';
import '@shared/dream-components/src/resetPasswordElement/ResetPassword.css';
import '@shared/dream-components/src/rssFeedSearchElement/RSSFeedSearch.css';
import '@shared/dream-components/src/rssFeedSearchElement/RSSFeedSearchModal.css';
import '@shared/dream-components/src/spacerElement/Spacer.css';
import '@shared/dream-components/src/surveyElement/Survey.css';
import '@shared/dream-components/src/updatePasswordElement/UpdatePassword.css';

// Rare blocks that carry their own code, named by node type.
const AdSlot = lazyBlock('adSlot', () => import('./nodes/AdSlot').then((module) => module.default));
const Authors = lazyBlock('authors', () => import('./nodes/Authors').then((module) => module.default));
const ContactForm = lazyBlock('contactForm', () => import('./nodes/ContactForm').then((module) => module.default));
const PodcastPlatforms = lazyBlock('podcastPlatforms', () =>
  import('./nodes/PodcastPlatforms').then((module) => module.default),
);
const PodcastTranscript = lazyBlock('podcastTranscript', () =>
  import('./nodes/PodcastTranscript').then((module) => module.default),
);
const PodcastVideo = lazyBlock('podcastVideo', () => import('./nodes/PodcastVideo').then((module) => module.default));
const PostNavigation = lazyBlock('postNavigation', () =>
  import('./nodes/PostNavigation').then((module) => module.default),
);
const ProductReviews = lazyBlock('productReviews', () =>
  import('./nodes/ProductReviews').then((module) => module.default),
);
const Products = lazyBlock('products', () => import('./nodes/Products').then((module) => module.default));
const ResetPassword = lazyBlock('resetPassword', () =>
  import('./nodes/ResetPassword').then((module) => module.default),
);
const RSSFeedSearch = lazyBlock('rss_feed_search', () =>
  import('./nodes/RSSFeedSearch').then((module) => module.default),
);
const Survey = lazyBlock('survey', () => import('./nodes/Survey').then((module) => module.default));
const UpdatePassword = lazyBlock('updatePassword', () =>
  import('./nodes/UpdatePassword').then((module) => module.default),
);

// Card and header structures bundle only their common types. The rest go through the full registry like a rare
// block, so they stay in the server HTML.
setUnmappedStructureElement(
  lazyBlock('StructureElement', () =>
    import('@shared/dream-components/src/serializer/FullRegistryElement').then((module) => module.default),
  ),
);

// Lazy so @stripe/react-stripe-js and the Upgrade/Signup machinery only load
// on pages that actually render a pricing node (see #17031 for the verified
// precedent).
const Pricing = lazy(() => import('./nodes/Pricing'));
// Lazy so the community route graph and comment upload UI stay out of every reader page's
// static closure; comments are fetched client-side, so SSR only loses the empty shell.
const loadPostComments = () => import('./nodes/PostComments');
const LazyPostComments = lazy(loadPostComments);
// The server renders #comments only on posts that show comments. Fetching the chunk alongside hydration lands
// comments in Chrome's post-navigation layout-shift grace window, as the old static import did.
if (typeof document !== 'undefined' && document.getElementById('comments')) loadPostComments().catch(() => {});

const COMMENTS_ANCHOR_SETTLE_MS = 10_000;
const READER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown', 'mousedown'] as const;

// Listens from module load, before hydration, so a reader who scrolls while the comments chunk is still loading is not pulled back.
let hasReaderScrolled = false;
const markReaderScrolled = () => {
  hasReaderScrolled = true;
};
if (typeof window !== 'undefined') {
  for (const eventName of READER_SCROLL_EVENTS)
    window.addEventListener(eventName, markReaderScrolled, { passive: true });
}

// The browser jumps to #comments before the real section and blocks below it have grown, so on a
// short page the jump clamps short. Re-anchor while comments load on slow networks, until the reader scrolls.
const ScrollToCommentsAnchor = () => {
  useEffect(() => {
    if (window.location.hash !== '#comments' || hasReaderScrolled) {
      hasReaderScrolled = false;
      return;
    }

    const scrollToComments = () => document.getElementById('comments')?.scrollIntoView();
    const resizeObserver = new ResizeObserver(scrollToComments);
    const stop = () => {
      resizeObserver.disconnect();
      clearTimeout(timeoutId);
      for (const eventName of READER_SCROLL_EVENTS) window.removeEventListener(eventName, stop);
    };
    const timeoutId = setTimeout(stop, COMMENTS_ANCHOR_SETTLE_MS);

    for (const eventName of READER_SCROLL_EVENTS) window.addEventListener(eventName, stop, { passive: true });
    resizeObserver.observe(document.body);
    scrollToComments();

    return () => {
      stop();
      hasReaderScrolled = false;
    };
  }, []);
  return null;
};

const PostComments = (props: ComponentProps<typeof LazyPostComments>) => {
  const { communityComments, showNativeComments } = getCommentsVisibility(usePostPageContext());
  // Read from context rather than useQueryClient, which throws outside the app's provider.
  const queryClient = useContext(QueryClientContext);
  const communityPostId = communityComments?.viewer_can_access ? communityComments.community_post_id : undefined;
  // Fetch the thread alongside the comments chunk, so it renders in one step instead of growing after mount.
  useEffect(() => {
    if (queryClient && communityPostId) {
      prefetchCommentThread(queryClient, communityPostId, window.location.hash.slice(1) || undefined);
    }
  }, [queryClient, communityPostId]);
  // Mounting the lazy node would fetch the comments chunk even on posts where it renders nothing.
  if (!communityComments && !showNativeComments) return null;
  const skeletonProps = { node: props.node, communityComments };

  return (
    <ComponentErrorBoundary fallbackRender={() => <PostCommentsSkeleton {...skeletonProps} isStatic />}>
      {/* Client-only: a server-resolved lazy would hydrate into a fallback flash and shift the #comments anchor. */}
      <ClientOnly fallback={<PostCommentsSkeleton {...skeletonProps} />}>
        {() => (
          <Suspense fallback={<PostCommentsSkeleton {...skeletonProps} />}>
            <LazyPostComments {...props} />
            <ScrollToCommentsAnchor />
          </Suspense>
        )}
      </ClientOnly>
    </ComponentErrorBoundary>
  );
};

interface Props {
  node: NodeContent;
  children?: ReactNode | ReactNode[];
  rootClassName?: string;
}

const NodeRenderer = ({ node, children, rootClassName }: Props) => {
  const isInSiteModalSlot = useContext(SiteModalSlotContext);

  if (SITE_MODAL_NODE_TYPES.includes(node.type) && !isInSiteModalSlot) {
    return null;
  }

  switch (node.type) {
    case 'accordion':
      return <Accordion node={node as TAccordionElement}>{children}</Accordion>;
    case 'accordionTitle':
      return <AccordionTitle node={node as TAccordionTitleElement}>{children}</AccordionTitle>;
    case 'accordionContent':
      return <AccordionContent node={node as TAccordionContentElement}>{children}</AccordionContent>;
    case 'accordionContainer':
      return <AccordionContainer node={node as TAccordionContainerElement}>{children}</AccordionContainer>;
    case 'adSlot':
      return <AdSlot node={node as TAdSlotElement} />;
    case 'audioPlayer':
      return <AudioPlayer node={node as TAudioPlayerElement} />;
    case 'section':
      return <Section node={node as TSectionElement}>{children}</Section>;
    case 'doc':
      return (
        <Doc node={node as unknown as TRootElement} className={rootClassName}>
          {children}
        </Doc>
      );
    case 'paragraph':
      return <Paragraph node={node as TParagraphElement}>{children}</Paragraph>;
    case 'text':
      return <Text node={node as TextLeafProps['leaf']}>{children}</Text>;
    case 'heading':
      return <Heading node={node as unknown as THeadingElement}>{children}</Heading>;
    case 'button':
      return <Button node={node as TButtonElement}>{children}</Button>;
    case 'container':
      return <Container node={node as TContainerElement}>{children}</Container>;
    case 'signup':
      return <Signup node={node as TSignupElement}>{children}</Signup>;
    case 'testimonials':
      return <Testimonials node={node as TTestimonialsElement}>{children}</Testimonials>;
    case 'productReviews':
      return <ProductReviews node={node as TProductReviewsElement}>{children}</ProductReviews>;
    case 'columns':
      return <Columns node={node as TColumnsElement}>{children}</Columns>;
    case 'column':
      return <Column node={node as TColumnElement}>{children}</Column>;
    case 'image':
      return <Image node={node as TImageElement}>{children}</Image>;
    case 'post':
      return <PostContainer node={node as TPostElement}>{children}</PostContainer>;
    case 'recommendedPosts':
      return <RecommendedPosts node={node as unknown as TRecommendedPostsElement}>{children}</RecommendedPosts>;
    case 'postNavigation':
      return <PostNavigation node={node as unknown as TPostNavigationElement} />;
    case 'postComments':
      return <PostComments node={node as unknown as TPostCommentsElement}>{children}</PostComments>;
    case 'socials':
      return <Socials node={node as TSocialsElement}>{children}</Socials>;
    case 'divider':
      return <Divider node={node as TDividerElement}>{children}</Divider>;
    case 'embed':
      return <Embed node={node as TEmbedElement}>{children}</Embed>;
    case 'authors':
      return <Authors node={node as TAuthorsElement}>{children}</Authors>;
    case 'pricing':
      // renderToString SSRs the Suspense fallback, so crawlers and first
      // paint get the height-reserving skeleton; the payment form renders
      // client-side (it re-renders from usePricingDetails data anyway).
      return (
        <ComponentErrorBoundary fallbackRender={() => <PricingSkeleton node={node as TPricingElement} />}>
          <Suspense fallback={<PricingSkeleton node={node as TPricingElement} />}>
            <Pricing node={node as TPricingElement}>{children}</Pricing>
          </Suspense>
        </ComponentErrorBoundary>
      );
    case 'contactForm':
      return <ContactForm node={node as TContactFormElement}>{children}</ContactForm>;
    case 'survey':
      return <Survey node={node as TSurveyElement}>{children}</Survey>;
    case 'signupModal':
      return <SignupModal node={node as unknown as TSignupModalElement}>{children}</SignupModal>;
    case 'recommendations':
      return <Recommendations node={node as unknown as TRecommendationsElement}>{children}</Recommendations>;
    case 'icon':
      return <Icon node={node as TIconElement}>{children}</Icon>;
    case 'recommendationsModal':
      return (
        <RecommendationsModal node={node as unknown as TRecommendationsModalElement}>{children}</RecommendationsModal>
      );
    case 'login':
      return <Login node={node as TLoginElement}>{children}</Login>;
    case 'resetPassword':
      return <ResetPassword node={node as TResetPasswordElement}>{children}</ResetPassword>;
    case 'updatePassword':
      return <UpdatePassword node={node as TUpdatePasswordElement}>{children}</UpdatePassword>;
    case 'loginModal':
      return <LoginModal node={node as unknown as TLoginModalElement}>{children}</LoginModal>;
    case 'tags':
      return <Tags node={node as TTagsElement}>{children}</Tags>;
    case 'hardBreak':
      return <br />;
    case 'proprietaryFooter':
      return <ProprietaryFooter node={node as unknown as ProprietaryFooterNode} />;
    case 'progressIndicator':
      return <ProgressIndicator node={node as TProgressIndicatorElement}>{children}</ProgressIndicator>;
    case 'postContent':
      return <PostContent node={node as TPostContentElement}>{children}</PostContent>;
    case 'postHeader':
      return <PostHeader node={node as TPostHeaderElement}>{children}</PostHeader>;
    case 'postActions':
      return <PostActions node={node as TPostActionsElement}>{children}</PostActions>;
    case 'emailCapturePopup':
      return <EmailCapturePopup node={node as unknown as TEmailCapturePopupElement}>{children}</EmailCapturePopup>;
    case 'contentGate':
      return <ContentGate node={node as unknown as TContentGateElement}>{children}</ContentGate>;
    case 'contentGatePopup':
      return <ContentGatePopup node={node as unknown as TContentGatePopupElement}>{children}</ContentGatePopup>;
    case 'emailCapture':
      return <EmailCapture node={node as unknown as TEmailCaptureElement}>{children}</EmailCapture>;
    case 'subscriberGate':
      return <SubscriberGate node={node as unknown as TSubscriberGateElement}>{children}</SubscriberGate>;
    case 'listItem':
      return <ListItem node={node as unknown as TListItemElement}>{children}</ListItem>;
    case 'bulletList':
      return <BulletedList node={node as unknown as TBulletedListElement}>{children}</BulletedList>;
    case 'orderedList':
      return <OrderedList node={node as unknown as TNumberedListElement}>{children}</OrderedList>;
    case 'rss_feed':
      return <RSSFeed node={node as unknown as TRSSFeedElement}>{children}</RSSFeed>;
    case 'rss_feed_search':
      return <RSSFeedSearch node={node as unknown as TRSSFeedSearchElement}>{children}</RSSFeedSearch>;
    case 'products':
      return <Products node={node as unknown as TProductsElement}>{children}</Products>;
    case 'richText':
      return <RichText node={node as unknown as TRichTextElement}>{children}</RichText>;
    case 'podcastPlatforms':
      return <PodcastPlatforms node={node as unknown as TPodcastPlatformsElement}>{children}</PodcastPlatforms>;
    case 'podcastTranscript':
      return <PodcastTranscript node={node as unknown as TPodcastTranscriptElement}>{children}</PodcastTranscript>;
    case 'podcastVideo':
      return <PodcastVideo node={node as unknown as TPodcastVideoElement}>{children}</PodcastVideo>;
    default:
      return null;
  }
};

export default NodeRenderer;
